<template>
  <v-card class="ps-card" flat>
    <div class="ps-card__head">
      <v-icon small>mdi-image-frame</v-icon>
      Print-Varianten
      <span class="ps-card__hint">Preise &amp; Fulfillment je Variante — Klick auf den Stift</span>
      <v-spacer />
      <v-btn
        v-if="archivedCount"
        text
        small
        rounded
        class="mr-2"
        @click="showArchived = !showArchived"
      >
        {{ showArchived ? "Archivierte ausblenden" : `Alle anzeigen (${archivedCount})` }}
      </v-btn>
      <v-btn @click="openAddNewPrintVariantDialog" color="primary" depressed small rounded>
        <v-icon small left>mdi-plus</v-icon>Variante
      </v-btn>
    </div>
    <v-card-text>
      <v-data-table
        :loading="loadingPrintVariants"
        :headers="headers"
        :sort-by="['name']"
        :items="visiblePrintVariants"
        :items-per-page="50"
        class="ps-table"
        :item-class="(item) => (item.isArchived ? 'archived-row' : '')"
      >
        <template v-slot:item.name="{ item }">
          <span class="ps-name">{{ item.name }}</span>
          <span v-if="item.isArchived" class="ps-archived-badge">
            <v-icon x-small color="#a96617">mdi-archive</v-icon>Archiv
          </span>
          <div class="ps-sub">{{ item.aspectRatio }}</div>
        </template>

        <template v-slot:item.production="{ item }">
          <div class="ps-shipping-lines">
            <div
              v-for="fk in Object.keys(item.fulfiller)"
              :key="fk"
              class="ps-total-line ps-total-line--neutral"
            >
              <span class="ps-total-line__name">{{ fulfillerName(fk) }}</span>
              <span class="ps-price">{{ formatPrice(item.fulfiller[fk].costs) }}</span>
              <span v-if="item.fulfiller[fk].express && item.fulfiller[fk].express.isAvailable" title="Express verfügbar">⚡</span>
            </div>
          </div>
        </template>

        <template v-slot:item.costs="{ item }">
          <div v-if="!fulfillerLines(item).length" class="ps-chip ps-chip--muted">
            kein Fulfiller festgelegt
          </div>
          <div class="ps-shipping-lines">
            <div
              v-for="line in fulfillerLines(item)"
              :key="line.cc"
              class="ps-total-line"
              :class="line.cls"
              :title="line.title"
            >
              <span class="ps-total-line__country">
                <template v-if="line.cc === 'default'">🌍</template>
                <template v-else>{{ flag(line.cc) }} {{ line.cc }}</template>
              </span>
              <span class="ps-total-line__name">{{ fulfillerName(line.fulfiller) }}</span>
              <span class="ps-price">{{ formatPrice(line.shipping) }}</span>
            </div>
          </div>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn small outlined rounded color="primary" @click="() => editPrintVariant(item)">
            <v-icon small left>mdi-pencil</v-icon>Bearbeiten
          </v-btn>
        </template>
      </v-data-table>
    </v-card-text>
    <PrintVariantDialog
      :show="!!showPrintVariantDialog"
      :on-close="closePrintVariantDialog"
      :print-variant="printVariantToEdit"
    />
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import PrintVariantDialog from "@/components/Settings/Products/PrintVariants/PrintVariantDialog";

const FULFILLER_NAMES = {
  picanova: "Picanova",
  picanovaUS: "merchOne US",
  myPoster: "MyPoster",
  doboxa: "Doboxa",
  manual: "Manuell",
};

export default {
  name: "PrintVariants",
  components: { PrintVariantDialog },
  data() {
    return {
      showPrintVariantDialog: null,
      printVariantToEdit: null,
      showArchived: false,
      headers: [
        { text: "Variante", value: "name" },
        { text: "Produktion", value: "production", sortable: false },
        { text: "Versand & Fulfiller je Land", value: "costs", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    };
  },
  computed: {
    ...mapGetters(["printVariants", "loadingPrintVariants"]),
    archivedCount() {
      return this.printVariants.filter((variant) => variant.isArchived).length;
    },
    visiblePrintVariants() {
      if (this.showArchived) return this.printVariants;
      return this.printVariants.filter((variant) => !variant.isArchived);
    },
  },
  mounted() {
    this.loadPrintVariants();
  },
  methods: {
    ...mapActions(["loadPrintVariants"]),
    flag(cc) {
      if (!/^[A-Z]{2}$/.test(cc)) return "";
      return String.fromCodePoint(
        ...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
      );
    },
    fulfillerName(key) {
      return FULFILLER_NAMES[key] || key;
    },
    formatPrice(value) {
      const num = Number(value) || 0;
      return num.toFixed(2).replace(".", ",") + " €";
    },
    /** Gesamtkosten eines Fulfillers für einen Land-Kontext:
     *  Produktion + Versand (Land, sonst Fallback) — Backend-Semantik. */
    totalFor(item, fk, cc) {
      const f = item.fulfiller[fk] || {};
      const sc = f.shippingCosts || {};
      const direct = cc === "default" ? undefined : sc[cc];
      const shipping = Number(direct ?? sc.default ?? 0) || 0;
      return (Number(f.costs) || 0) + shipping;
    },
    /** Nur Fulfiller mit gepflegtem Versandpreis (Land oder Fallback) dürfen
     *  am Günstigkeits-Vergleich teilnehmen — sonst "gewinnen" Fulfiller,
     *  die das Land gar nicht beliefern, mit fälschlichen 0 €. */
    hasShippingPrice(item, fk, cc) {
      const sc = item.fulfiller[fk]?.shippingCosts || {};
      if (sc.default !== undefined) return true;
      return cc !== "default" && sc[cc] !== undefined;
    },
    /** Eine Zeile je Land-Kontext: Default + Länder-Regeln, jeweils mit dem
     *  dort gewählten Fulfiller, dessen Gesamtpreis und Günstigkeits-Status. */
    fulfillerLines(item) {
      const contexts = [];
      if (this.defaultRank(item).length) contexts.push("default");
      contexts.push(...this.countryOverrides(item));
      return contexts.map((cc) => {
        const rank =
          cc === "default" ? this.defaultRank(item) : item.fulfillRank[cc];
        const active = rank[0];
        const sc = item.fulfiller[active]?.shippingCosts || {};
        const direct = cc === "default" ? undefined : sc[cc];
        const shipping = Number(direct ?? sc.default ?? 0) || 0;
        // Anzeige: Versandpreis — Vergleich: Gesamtkosten (Produktion+Versand),
        // denn ein Fulfiller-Wechsel ändert beide
        const total = this.totalFor(item, active, cc);
        const candidates = Object.keys(item.fulfiller).filter((key) =>
          this.hasShippingPrice(item, key, cc)
        );
        const cheapest = candidates.length
          ? Math.min(...candidates.map((key) => this.totalFor(item, key, cc)))
          : total;
        const best = total <= cheapest;
        // 0 € beim gewählten Fulfiller = fehlender/kaputter Versandpreis
        const isError = !shipping;
        return {
          cc,
          fulfiller: active,
          shipping,
          cls: isError
            ? "ps-total-line--error"
            : best
            ? "ps-total-line--best"
            : "ps-total-line--pricier",
          title: isError
            ? "FEHLER: Kein Versandpreis gepflegt — Bestellungen laufen mit 0 € Versand in die Statistik"
            : best
            ? "Gesamt (Produktion + Versand) günstigste Wahl für dieses Land"
            : "Ein anderer Fulfiller wäre für dieses Land gesamt günstiger",
        };
      });
    },
    defaultRank(item) {
      return item.fulfillRank?.default || [];
    },
    countryOverrides(item) {
      return Object.keys(item.fulfillRank || {})
        .filter((cc) => cc !== "default")
        .sort();
    },
    editPrintVariant(printVariant) {
      this.printVariantToEdit = printVariant;
      this.showPrintVariantDialog = true;
    },
    openAddNewPrintVariantDialog() {
      this.printVariantToEdit = null;
      this.showPrintVariantDialog = true;
    },
    closePrintVariantDialog() {
      this.printVariantToEdit = null;
      this.showPrintVariantDialog = false;
    },
  },
};
</script>

<style scoped></style>
